// A name that goes somewhere (B-220, SCENARIOS K6): getting around should be
// easy from anywhere it makes sense to.
//
// A PLAYER'S NAME OPENS HIS CARD, a team's name opens its page
// (`/l/:id/teams/:teamId`), on every screen that shows one — the same two
// doors everywhere, so a name is never sometimes a link and sometimes text.
//
// TWO LOOKS FOR ONE LINK. Where a name stands in a name's place — a row's
// bold line, a heading — it looks as it did and underlines under a pointer.
// Where it sits inside a sentence ("Pacheco questionable with a hamstring",
// "Rico's Team sent you an offer") it carries a faint dotted underline, since
// nothing else in the sentence says it can be tapped.
//
// NEVER INSIDE ANOTHER BUTTON. A row that is itself a button (a scoreboard
// game opens its matchup) cannot hold one: the screen takes the row's own
// door out of the row instead. The click here stops at the name, for rows
// that answer a click without being a button.

import type { MouseEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeagueBase } from '../../data/hooks';
import { useOpenPlayer } from '../../shell/playerCard';
import { morph } from '../../shell/morph';
import type { PlayerRef } from './PlayerCard';
import { ProTeamCode } from '../../kit';

type Look = 'name' | 'inline';

function classOf(look: Look, className?: string): string {
  return `fsp-linklike fsp-name-link${look === 'inline' ? ' fsp-name-link-inline' : ''}${className ? ` ${className}` : ''}`;
}

/** "UR: Uncle Rico's Team" for a name shown short, and nothing when it is shown whole. */
function shortened(shown: ReactNode, whole: string): string | undefined {
  return typeof shown === 'string' && shown !== whole ? `${shown}: ${whole}` : undefined;
}

/**
 * A player's name that opens his card, and his club beside it when the caller
 * has it (B-510): `club` is his `proTeam`, null for a free agent.
 */
export function PlayerName({
  player,
  look = 'name',
  className,
  club,
  children,
}: {
  player: PlayerRef;
  look?: Look;
  className?: string;
  /** His club (`PlayerSummary.proTeam`), shown after the name; absent, none is shown. */
  club?: string | null;
  /** What to show instead of his full name — a short name, say. */
  children?: ReactNode;
}) {
  const open = useOpenPlayer();
  const name = (
    <button
      type="button"
      className={classOf(look, className)}
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        open({ id: player.id, name: player.name });
      }}
      title={`${player.name}'s card`}
      // A SHORT NAME SAYS THE WHOLE ONE TOO: a dense grid shows "P.Nacua",
      // and its name is "P.Nacua: Puka Nacua", the visible words first
      // (UX_STANDARDS UX-11, WCAG 2.5.3).
      aria-label={shortened(children, player.name)}
    >
      {children ?? player.name}
    </button>
  );
  if (club === undefined) return name;
  return (
    <>
      {name}
      {/* A space a sentence keeps and a flex row drops. */} <ProTeamCode code={club} />
    </>
  );
}

/** A fantasy team's name that opens its page. */
export function TeamName({
  team,
  look = 'name',
  className,
  base: given,
  onGo,
  children,
}: {
  team: { id: string; name: string };
  look?: Look;
  className?: string;
  /** Another league's prefix, where the name is not in the league on screen (the hub's cards). */
  base?: string;
  /**
   * Run before going: a sheet the name sits in closes itself, since a screen
   * that stays mounted from one team's page to the next keeps its sheet open.
   */
  onGo?: () => void;
  children?: ReactNode;
}) {
  const navigate = useNavigate();
  const here = useLeagueBase();
  const base = given ?? here;
  return (
    <button
      type="button"
      className={classOf(look, className)}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onGo?.();
        // THE NAME GROWS INTO ITS PAGE'S HEADING (B-404): the same name,
        // moved, rather than a new page with it somewhere on it.
        const hero = e.currentTarget;
        morph(() => navigate(`${base}/teams/${team.id}`), { motion: 'deeper', hero });
      }}
      title={`${team.name}'s roster`}
      aria-label={shortened(children, team.name)}
    >
      {children ?? team.name}
    </button>
  );
}

/**
 * A sentence with the names in it linked: the recap's lines (B-220), whose
 * server says where each name sits. A span that does not fit the text — an
 * older answer, a server bug — is left as words rather than cutting the
 * sentence somewhere wrong.
 */
export function LinkedText({
  text,
  names,
}: {
  text: string;
  /** A player's `club`, where the caller has it, rides after his name (B-510). */
  names: readonly {
    kind: 'player' | 'team';
    id: string;
    start: number;
    end: number;
    club?: string | null;
  }[];
}) {
  const parts: ReactNode[] = [];
  let at = 0;
  for (const n of [...names].sort((a, b) => a.start - b.start)) {
    if (n.start < at || n.end > text.length || n.end <= n.start) continue;
    if (n.start > at) parts.push(text.slice(at, n.start));
    const words = text.slice(n.start, n.end);
    parts.push(
      n.kind === 'player' ? (
        <PlayerName
          key={n.start}
          player={{ id: n.id, name: words }}
          look="inline"
          {...(n.club === undefined ? {} : { club: n.club })}
        />
      ) : (
        <TeamName key={n.start} team={{ id: n.id, name: words }} look="inline" />
      ),
    );
    at = n.end;
  }
  if (at < text.length) parts.push(text.slice(at));
  return <>{parts}</>;
}

/**
 * Where the given players' names sit in a server's sentence, for
 * `LinkedText`: the waiver log's lines ("Rico added Pacheco, dropped Hall"),
 * whose rows carry the players beside the words. A whole-word match on his
 * full name, else his short one; a name two of them share is left as words.
 */
export function playerSpans(
  text: string,
  players: readonly { id: string; name: string; shortName?: string; proTeam?: string | null }[],
): { kind: 'player'; id: string; start: number; end: number; club?: string | null }[] {
  const out: { kind: 'player'; id: string; start: number; end: number; club?: string | null }[] =
    [];
  const clubs = new Map(players.map((p) => [p.id, p.proTeam]));
  const word = /[\p{L}\p{N}]/u;
  const forms = players.flatMap((p) =>
    [p.name, p.shortName ?? ''].filter((f) => f !== '').map((f) => ({ id: p.id, f })),
  );
  const shared = (f: string) => new Set(forms.filter((x) => x.f === f).map((x) => x.id)).size > 1;
  // Longest first, so a full name is taken before the short one inside it.
  for (const { id, f } of [...forms].sort((a, b) => b.f.length - a.f.length)) {
    if (shared(f)) continue;
    let from = 0;
    for (;;) {
      const start = text.indexOf(f, from);
      if (start < 0) break;
      const end = start + f.length;
      from = end;
      if (word.test(text[start - 1] ?? '') || word.test(text[end] ?? '')) continue;
      if (out.some((o) => start < o.end && end > o.start)) continue;
      const club = clubs.get(id);
      out.push({ kind: 'player', id, start, end, ...(club === undefined ? {} : { club }) });
    }
  }
  return out.sort((a, b) => a.start - b.start);
}
